import React, { useState, useEffect, useRef, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { ReactEditor, useSlate } from 'slate-react';
import {
  Editor,
  Range,
  Element as SlateElement,
  Transforms
} from 'slate';

import { mount, unmount } from 'redom';
import { useStore } from '@nanostores/react';

import application from 'application';
import serverInterface from 'server-interface';

import internalEventCodes from 'event-system/codes/internal';

import useOnClickOutside from 'generics/hooks/on-click-outside';
import useOnEscapeKeypress from 'generics/hooks/on-escape-key';

import Portal from 'generics/components/portal';
import HelptapColorPicker from 'generics/components/color-picker';
import FontSizeInput from 'generics/components/font-size-input/react';

import {
  displayElementAroundAnchor
} from 'utilities/element-position';

import { toggleBlock } from '../3rd-party/rich-text';
import types from '../elements/types';
import Helpers from '../helpers';
import { ensureEditorStores } from '../stores/active-block';
import { getSelectionRectangle, positionToolbarColorOptions } from '../utilities';

import LinkURLInputBox from './link-url-input-box/native';
import FontFamilyButton from './font-family-button';
import FontWeightSelector from './font-weight-selector';
import ToolGroup from './tool-group';
import ToolbarAnchor from './anchor/react';
import AIInstructionInput from './ai-instruction-input/native';
import AIInstructionResultOverlay from './ai-instruction-input/result-overlay';

import {
  applyReplacementToSelection,
  buildAIRequestPayload,
  getSelectedSingleBlock,
  isValidReplaceSelectionResponse
} from './ai-instruction-input/utilities';

import {
  setToolbarPosition
} from './utilities';

import { ReactComponent as ToolbarTextColorIcon } from 'assets/images/text-color-icon.svg';
import { ReactComponent as ToolbarBackgroundColorIcon } from 'assets/images/text-background-color-icon.svg';
import { ReactComponent as ToolbarQuoteTextIcon } from 'assets/images/quote-text-toggle-icon.svg';
import { ReactComponent as ToolbarLinkIcon } from 'assets/images/link-toggle-icon.svg';
import { ReactComponent as UnformatIcon } from 'assets/images/unformat-icon.svg';
import { ReactComponent as FontSizeIcon } from 'assets/images/font-size-icon.svg';
import { ReactComponent as FontWeightIcon } from 'assets/images/font-weight-icon.svg';
import { ReactComponent as LineHeightIcon } from 'assets/images/line-height-icon.svg';
import { ReactComponent as AskAIIcon } from 'assets/images/ask-ai-icon.svg';

import actionTypes from './action-types';

function Toolbar(props) {

  const editorMainReference = props.editorMainReference;
  const viewportReference = props.viewportReference;

  const editor = useSlate();

  const { nodeMenu } = ensureEditorStores(editor);
  const nodeMenuState = useStore(nodeMenu);

  const toolbarReference = useRef(null);
  const toolbarTextColorOptionsReference = useRef(null);
  const toolbarBackgroundColorOptionsReference = useRef(null);

  const colorButtonsAchorReference = useRef(null);
  const fontButtonsAchorReference = useRef(null);
  const dynamicContentContainerRefrence = useRef(null);
  const urlInputDisplayReference = useRef(false);

  const aiInputComponentReference = useRef(null);
  const aiInputContainerNodeReference = useRef(null);
  const savedAISelectionReference = useRef(null);
  const aiInteractionIdReference = useRef(0);
  const aiInputEngagedReference = useRef(false);
  const aiActiveResponseReference = useRef(null);
  const toolInputEngagedReference = useRef(false);
  const savedToolSelectionReference = useRef(null);
  const aiPanelVisibleReference = useRef(false);
  const aiResultOverlayReference = useRef(null);
  const aiInputSubmitHandlerReference = useRef(null);
  const aiInputCaptureHandlerReference = useRef(null);
  const aiInputAcceptHandlerReference = useRef(null);
  const aiInputDiscardHandlerReference = useRef(null);
  const aiInputConfirmHandlerReference = useRef(null);
  const aiInputCancelHandlerReference = useRef(null);

  const [selectionRectangle, setSelectionRectangle] = useState(null);
  const [rootState, setRootState] = useState({
    toolbarDisplayedSide: false,
    lastScrolledAt: Date.now(),
    displayURLInputBox: false,
    selectionRectangle: null,
    ...getDefaultButtonStates(),
    currentNode: null,
    connectedPageLinkButtonWorking: false
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleToolbarButtonClick = useCallback(_handleToolbarButtonClick, []);

  const positionToolbar = useCallback(function positionToolbar() {

    const element = toolbarReference.current;
    const { selection } = editor;

    if (props.readOnly === true) {
      return;
    }

    if (element === null) {
      return;
    }

    if (urlInputDisplayReference.current === true) {
      return;
    }

    if (selection === null) {
      if (isInteractionEngaged() === true) {
        return;
      }
      return hideToolbarElement(element);
    }

    // if (ReactEditor.isFocused(editor) === false) {
    //   return hideToolbarElement(element);
    // }

    if (Range.isCollapsed(selection) === true) {
      if (isInteractionEngaged() === true) {
        return;
      }
      return hideToolbarElement(element);
    }

    if (Editor.string(editor, selection) === '') {
      if (isInteractionEngaged() === true) {
        return;
      }
      return hideToolbarElement(element);
    }

    // If multi line selection, no toolbar
    const multiLineSelection = Helpers.isMultilineSelection(editor);
    const onlyTextNodesSelected = Helpers.isOnlyTextNodesSelected(editor);

    if (multiLineSelection === true && onlyTextNodesSelected === false) {
      return hideToolbarElement(element);
    }

    try {

      const [node] = Editor.above(editor, {
        mode: 'lowest'
      });

      setCurrentNode(node);

    } catch (exception) {

      setCurrentNode(null);

    }
    const selectionRectangle = getSelectionRectangle();

    if (selectionRectangle === null) {
      return;
    }

    element.style.opacity = '1';
    setTimeout(function someTimeLater() {
      const toolbarDisplayedSide = setToolbarPosition(element, editorMainReference);
      setToolbarDisplayedSide(toolbarDisplayedSide);
    }, 10);

    setSelectionRectangle(JSON.stringify({
      bottom: selectionRectangle.bottom,
      height: selectionRectangle.height,
      left: selectionRectangle.left,
      right: selectionRectangle.right,
      top: selectionRectangle.top,
      width: selectionRectangle.width,
      x: selectionRectangle.x,
      y: selectionRectangle.y
    }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor.selection]);

  useOnEscapeKeypress(handleEscapeKey);
  useOnClickOutside(toolbarReference, handleOutsideClick);

  useEffect(function onLoad() {

    return positionOnScroll();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(positionToolbar, [editor.selection]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(positionToolbar, [nodeMenuState.isOpen]);

  function setAIPanelVisible(visible) {

    if (aiPanelVisibleReference.current === visible) {
      return;
    }

    aiPanelVisibleReference.current = visible;

    const container = aiInputContainerNodeReference.current;

    if (container === null) {
      return;
    }

    if (visible === true) {
      container.classList.remove('hidden');
      return;
    }

    container.classList.add('hidden');

  }

  function setAISelectionHighlight(range) {

    if (typeof props.setAISelectionHighlight !== 'function') {
      return;
    }

    props.setAISelectionHighlight(range);

  }

  function clearAISelectionHighlight() {

    setAISelectionHighlight(null);

  }

  useEffect(function updateAIInputState() {

    if (props.aiInstructionEnabled !== true) {
      return;
    }

    if (aiInputEngagedReference.current === true) {
      return;
    }

    const selection = editor.selection;

    if (selection === null) {
      setAIPanelVisible(false);
      return;
    }

    if (Range.isCollapsed(selection) === true) {
      setAIPanelVisible(false);
      return;
    }

    if (Editor.string(editor, selection) === '') {
      setAIPanelVisible(false);
      return;
    }

    const singleBlock = getSelectedSingleBlock(editor, selection);

    if (singleBlock === null) {
      setAIPanelVisible(false);
      savedAISelectionReference.current = null;
      return;
    }

    savedAISelectionReference.current = {
      anchor: selection.anchor,
      focus: selection.focus
    };
    setAIPanelVisible(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor.selection]);

  useEffect(function positionTextColorOptions() {

    const element = toolbarTextColorOptionsReference.current;

    if (element === null) {
      return;
    }

    positionToolbarTextAndBackgroundColorOptions(element);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolbarTextColorOptionsReference.current, rootState.displayTextColorOptions]);

  useEffect(function positionBackgroundColorOptions() {

    const element = toolbarBackgroundColorOptionsReference.current;

    if (element === null) {
      return;
    }

    positionToolbarTextAndBackgroundColorOptions(element);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolbarBackgroundColorOptionsReference.current, rootState.displayBackgroundColorOptions]);

  const handleURLChange = useCallback(function _handleURLChange(url) {
    Helpers.updateLink(editor, url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hideURLInputBox = useCallback(function hideURLInputBox() {
    urlInputDisplayReference.current = false;
  }, []);

  const showURLInputBox = useCallback(function showURLInputBox() {
    urlInputDisplayReference.current = true;
  }, []);

  function getDefaultButtonStates() {

    const state = {
      displayTextColorOptions: false,
      displayBackgroundColorOptions: false,
      displayFontFamilySelector: false,
      displayFontSizeSelector: false,
      displayFontWeightSelector: false,
      displayLineHeightSelector: false,
      displayColorButtons: false,
      displayFontButtons: false
    };

    return state;

  }

  function setConnectedPageLinkButtonWorkingStatus(status) {

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        connectedPageLinkButtonWorking: status
      };

    });

  }

  function setCurrentNode(currentNode) {

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        currentNode
      };

    });

  }

  function setToolbarDisplayedSide(toolbarDisplayedSide) {

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        toolbarDisplayedSide
      };

    });

  }

  function updateLastScrolledAt() {

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        lastScrolledAt: Date.now()
      };

    });

  }

  function positionOnScroll() {

    const element = editorMainReference.current;

    function editorScrollHandler() {
      positionToolbar();
      updateLastScrolledAt();
    }

    element.addEventListener('scroll', editorScrollHandler);

    return function removeEventListener() {
      element.removeEventListener('scroll', editorScrollHandler);
    };

  }

  function positionToolbarTextAndBackgroundColorOptions(element) {

    const toolbar = toolbarReference.current;

    if (toolbar === null) {
      return;
    }

    const anchorRectangle = toolbar.getBoundingClientRect();

    positionToolbarColorOptions(element, viewportReference, anchorRectangle);

  }

  function hideToolbarElement(element) {

    element.removeAttribute('style');

  }

  function handleOutsideClick(event) {

    const overlay = aiResultOverlayReference.current;

    if (overlay !== null && overlay.el.contains(event.target) === true) {
      return;
    }

    hideToolbar();

  }

  function handleTextColorPickerClose() {

    toggleTextColorOptionsDisplay();

  }

  function handleBackgroundColorPickerClose() {

    toggleBackgroundColorOptionsDisplay();

  }

  function handleEscapeKey() {

    if (rootState.displayTextColorOptions === true) {
      return handleTextColorPickerClose();
    } else if (rootState.displayBackgroundColorOptions === true) {
      return handleBackgroundColorPickerClose();
    } else if (rootState.displayFontFamilySelector === true) {
      return hideFontFamilySelector();
    } else if (rootState.displayFontSizeSelector === true) {
      return hideFontSizeSelector();
    } else if (rootState.displayFontWeightSelector === true) {
      return hideFontWeightSelector();
    } else if (rootState.displayLineHeightSelector === true) {
      return hideLineHeightSelector();
    } else if (rootState.displayColorButtons === true) {
      return toggleColorButtons();
    } else if (rootState.displayFontButtons === true) {
      return toggleFontButtons();
    }

    hideToolbar();

  }

  function hideToolbar() {

    const element = toolbarReference.current;

    if (element === null) {
      return;
    }

    element.style.top = '-999px';
    element.style.left = '-999px';
    element.style.opacity = 0;

    aiInputEngagedReference.current = false;
    aiInteractionIdReference.current += 1;
    aiActiveResponseReference.current = null;
    savedAISelectionReference.current = null;

    disengageToolInput();

    clearAISelectionHighlight();

    unmountResultOverlay();

    const aiInputComponent = aiInputComponentReference.current;

    if (aiInputComponent !== null) {
      aiInputComponent.clearInput();
    }

    setAIPanelVisible(false);

    setRootState({
      ...rootState,
      ...getDefaultButtonStates()
    });

  }

  function _handleToolbarButtonClick(type) {

    if (type === actionTypes.BOLD) {
      Helpers.toggleBoldMark(editor);
    } else if (type === actionTypes.ITALIC) {
      Helpers.toggleItalicMark(editor);
    } else if (type === actionTypes.UNDERLINE) {
      Helpers.toggleUnderlineMark(editor);
    } else if (type === actionTypes.CODE_BLOCK) {
      Helpers.toggleCodeBlock(editor);
    } else if (type === actionTypes.STRIKE_THROUGH) {
      Helpers.toggleStrikeThroughMark(editor);
    } else if (type === actionTypes.BULLET_LIST) {
      toggleBlock(editor, 'bulleted-list');
    } else if (type === actionTypes.NUMBER_LIST) {
      toggleBlock(editor, 'numbered-list');
    } else if (type === actionTypes.CHECK_LIST) {
      toggleBlock(editor, 'check-list-item');
    } else if (type === actionTypes.QUOTE_TEXT) {
      toggleBlock(editor, 'block-quote');
    } else if (type === actionTypes.HEADING_ONE) {
      toggleBlock(editor, 'heading-one');
    } else if (type === actionTypes.HEADING_TWO) {
      toggleBlock(editor, 'heading-two');
    } else if (type === actionTypes.HEADING_THREE) {
      toggleBlock(editor, 'heading-three');
    } else if (type === actionTypes.HEADING_FOUR) {
      toggleBlock(editor, 'heading-four');
    } else if (type === actionTypes.HEADING_FIVE) {
      toggleBlock(editor, 'heading-five');
    } else if (type === actionTypes.ALIGN_LEFT) {
      Helpers.toggleAlignmentMark(editor, 'left');
    } else if (type === actionTypes.ALIGN_CENTER) {
      Helpers.toggleAlignmentMark(editor, 'center');
    } else if (type === actionTypes.ALIGN_RIGHT) {
      Helpers.toggleAlignmentMark(editor, 'right');
    }

  }

  function handleToggleLinkButtonClick(event) {

    hideToolbar();
    showURLInputBox();

    const domRange = ReactEditor.toDOMRange(editor, editor.selection);

    let url = '';
    const [link] = Helpers.getLinkNodes(editor);

    if (link !== void 0) {
      url = link[0].url;
    }

    const inputBox = new LinkURLInputBox({
      url,
      onChange: handleURLChange,
      onClose() {
        unmount(document.body, inputBox);
        hideURLInputBox();
        Transforms.collapse(editor, { edge: 'end' });
        ReactEditor.focus(editor);
      }
    });

    mount(document.body, inputBox);

    const element = inputBox.el;
    const anchorElement = domRange;
    const viewportElement = void 0;
    const verticalOffset = 0;
    const horizontalOffset = 5;
    const startSide = 'top';
    const placeOnCenter = true;
    const positionableSides = ['top', 'bottom'];

    displayElementAroundAnchor(element, anchorElement, viewportElement, verticalOffset, horizontalOffset, startSide, placeOnCenter, positionableSides);
    displayElementAroundAnchor(element, anchorElement, viewportElement, verticalOffset, horizontalOffset, startSide, placeOnCenter, positionableSides);

  }

  async function handleToggleConnectedPageLinkButtonClick() {

    setConnectedPageLinkButtonWorkingStatus(true);
    const connected = await Helpers.dataCollection.processConnectLinkRequest(editor, props.parentEntityId);
    setConnectedPageLinkButtonWorkingStatus(false);

    if (connected === false) {
      application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
        message: 'Links removed',
        seconds: 5,
        type: 'info'
      });
    }

  }

  function handleColorSelection(attribute, color) {

    applyTextAttributeWithSelection(attribute, color);

  }

  function toggleTextColorOptionsDisplay() {

    const displayTextColorOptions = rootState.displayTextColorOptions === false;

    setRootState({
      ...rootState,
      ...getDefaultButtonStates(),
      displayTextColorOptions
    });

  }

  function toggleBackgroundColorOptionsDisplay() {

    const displayBackgroundColorOptions = rootState.displayBackgroundColorOptions === false;

    setRootState({
      ...rootState,
      ...getDefaultButtonStates(),
      displayBackgroundColorOptions
    });

  }

  function handleUnformatControlClick() {

    Helpers.removeFormatting(editor);

  }

  function tryAddMarkActiveClass(buttonAttributes, type) {

    let isActive = false;

    if (type === actionTypes.QUOTE_TEXT) {
      isActive = Helpers.isBlockActive(editor, 'block-quote');
    }

    if (isActive === true) {

      buttonAttributes.className += ' mark-active';

    }

  }

  function showFontFamilySelector() {

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        ...getDefaultButtonStates(),
        displayFontFamilySelector: true
      };

    });

  }

  function hideFontFamilySelector() {

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        ...getDefaultButtonStates()
      };

    });

  }

  function showFontSizeSelector() {

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        ...getDefaultButtonStates(),
        displayFontSizeSelector: true
      };

    });

  }

  function hideFontSizeSelector() {

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        ...getDefaultButtonStates()
      };

    });

  }

  function showFontWeightSelector() {

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        ...getDefaultButtonStates(),
        displayFontWeightSelector: true
      };

    });

  }

  function hideFontWeightSelector() {

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        ...getDefaultButtonStates()
      };

    });

  }

  function toggleFontFamilySelector() {

    if (rootState.displayFontFamilySelector === true) {
      hideFontFamilySelector();
    } else {
      showFontFamilySelector();
    }

  }

  function toggleFontSizeSelector() {

    if (rootState.displayFontSizeSelector === true) {
      hideFontSizeSelector();
    } else {
      showFontSizeSelector();
    }

  }

  function toggleFontWeightSelector() {

    if (rootState.displayFontWeightSelector === true) {
      hideFontWeightSelector();
    } else {
      showFontWeightSelector();
    }

  }

  function showLineHeightSelector() {

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        ...getDefaultButtonStates(),
        displayLineHeightSelector: true
      };

    });

  }

  function hideLineHeightSelector() {

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        ...getDefaultButtonStates()
      };

    });

  }

  function toggleLineHeightSelector() {

    if (rootState.displayLineHeightSelector === true) {
      hideLineHeightSelector();
    } else {
      showLineHeightSelector();
    }

  }

  function toggleAnchorButtonsByPropertyName(propertyName) {

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        ...getDefaultButtonStates(),
        [propertyName]: rootState[propertyName] === false
      };

    });

  }

  function toggleColorButtons() {

    toggleAnchorButtonsByPropertyName('displayColorButtons');

  }

  function toggleFontButtons() {

    toggleAnchorButtonsByPropertyName('displayFontButtons');

  }

  function isTextNode() {

    return [types.TABLE_CELL, 'text'].indexOf(rootState.currentNode.type) !== -1;

  }

  function focusAssistantMessageBox() {

    hideToolbar();

    application.assistantManager.assistant.focus();

  }

  function handleAIInputCapture() {

    aiInputEngagedReference.current = true;

    if (savedAISelectionReference.current !== null) {
      setAISelectionHighlight(savedAISelectionReference.current);
    }

  }

  function mountResultOverlay() {

    if (aiResultOverlayReference.current !== null) {
      return;
    }

    const overlay = new AIInstructionResultOverlay({
      onAccept: function onAccept() {
        aiInputAcceptHandlerReference.current();
      },
      onDiscard: function onDiscard() {
        aiInputDiscardHandlerReference.current();
      }
    });

    mount(document.body, overlay);

    const toolbarElement = toolbarReference.current;

    if (toolbarElement !== null) {
      overlay.positionAt(toolbarElement.getBoundingClientRect());
      toolbarElement.style.visibility = 'hidden';
    }

    aiResultOverlayReference.current = overlay;

  }

  function unmountResultOverlay() {

    const overlay = aiResultOverlayReference.current;

    if (overlay === null) {
      return;
    }

    unmount(document.body, overlay);

    const toolbarElement = toolbarReference.current;

    if (toolbarElement !== null) {
      toolbarElement.style.visibility = '';
    }

    aiResultOverlayReference.current = null;

  }

  function resetAIInteraction() {

    aiInteractionIdReference.current += 1;
    aiActiveResponseReference.current = null;
    aiInputEngagedReference.current = false;

    unmountResultOverlay();

    const aiInputComponent = aiInputComponentReference.current;

    if (aiInputComponent !== null) {
      aiInputComponent.clearInput();
    }

  }

  async function handleAIInputSubmit(instruction) {

    const savedSelection = savedAISelectionReference.current;

    if (savedSelection === null) {
      return;
    }

    const singleBlock = getSelectedSingleBlock(editor, savedSelection);

    if (singleBlock === null) {
      return;
    }

    const payload = buildAIRequestPayload(editor, instruction, savedSelection, singleBlock);

    console.log('AI request payload:', payload);

    aiInteractionIdReference.current += 1;
    const interactionId = aiInteractionIdReference.current;

    aiInputEngagedReference.current = true;
    aiActiveResponseReference.current = null;

    mountResultOverlay();
    aiResultOverlayReference.current.setPendingState();

    const response = await serverInterface.startup.pages.generateTextReplacements(payload);

    if (interactionId !== aiInteractionIdReference.current) {
      return;
    }

    aiActiveResponseReference.current = response;
    aiResultOverlayReference.current.setReviewState(response);

  }

  function handleAIInputAccept() {

    const response = aiActiveResponseReference.current;

    if (response === null) {
      return;
    }

    if (isValidReplaceSelectionResponse(response) === false) {
      return;
    }

    const savedSelection = savedAISelectionReference.current;

    if (savedSelection === null) {
      return;
    }

    ReactEditor.focus(editor);
    applyReplacementToSelection(editor, savedSelection, response.replacements);

    clearAISelectionHighlight();

    resetAIInteraction();

  }

  function handleAIInputDiscard() {

    resetAIInteraction();

  }

  function handleAIInputConfirm() {

    if (aiActiveResponseReference.current === null) {
      return;
    }

    handleAIInputAccept();

  }

  function handleAIInputCancel() {

    handleAIInputDiscard();

  }

  aiInputSubmitHandlerReference.current = handleAIInputSubmit;
  aiInputCaptureHandlerReference.current = handleAIInputCapture;
  aiInputAcceptHandlerReference.current = handleAIInputAccept;
  aiInputDiscardHandlerReference.current = handleAIInputDiscard;
  aiInputConfirmHandlerReference.current = handleAIInputConfirm;
  aiInputCancelHandlerReference.current = handleAIInputCancel;

  const handleAIInputContainerReference = useCallback(function handleAIInputContainerReference(node) {

    if (node !== null && aiInputComponentReference.current === null) {

      const component = new AIInstructionInput({
        onSubmit: function onSubmit(instruction) {
          aiInputSubmitHandlerReference.current(instruction);
        },
        onCapture: function onCapture() {
          aiInputCaptureHandlerReference.current();
        },
        onConfirm: function onConfirm() {
          aiInputConfirmHandlerReference.current();
        },
        onCancel: function onCancel() {
          aiInputCancelHandlerReference.current();
        }
      });

      mount(node, component);

      aiInputComponentReference.current = component;
      aiInputContainerNodeReference.current = node;

      return;

    }

    if (node === null && aiInputComponentReference.current !== null) {

      unmountResultOverlay();
      unmount(aiInputContainerNodeReference.current, aiInputComponentReference.current);

      aiInputComponentReference.current = null;
      aiInputContainerNodeReference.current = null;
      aiInputEngagedReference.current = false;
      aiInteractionIdReference.current += 1;
      aiActiveResponseReference.current = null;
      savedAISelectionReference.current = null;

      clearAISelectionHighlight();

    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function renderIconByName(iconName) {

    if (iconName === 'quote-text-toggle-icon') {
      return <ToolbarQuoteTextIcon className={iconName} />;
    }

  }

  function renderButton(type, iconClassName, buttonClassName = '', tooltip = '') {

    const buttonAttributes = {
      className: 'inline-toolbar-button ' + buttonClassName,
      onClick() {
        handleToolbarButtonClick(type);
      }
    };

    tryAddMarkActiveClass(buttonAttributes, type);

    return <x-tooltip text={tooltip}>
      <button {...buttonAttributes}>
        {renderIconByName(iconClassName)}
      </button>
    </x-tooltip>;

  }

  function renderAskAIButton() {

    const buttonAttributes = {
      className: 'inline-toolbar-button ask-ai-button',
      onClick() {
        focusAssistantMessageBox();
      }
    };

    return <x-tooltip text="Assistant">
      <button {...buttonAttributes}>
        <AskAIIcon className="ask-ai-icon" />
      </button>
    </x-tooltip>;

  }

  function renderHeadingButtonsAnchor() {

    const anchorProperties = {
      editor,
      dynamicContentContainerRefrence,
      group: 'headings',
      marks: [
        { type: actionTypes.HEADING_ONE, active: Helpers.isBlockActive(editor, 'heading-one'), tooltip: 'Heading one' },
        { type: actionTypes.HEADING_TWO, active: Helpers.isBlockActive(editor, 'heading-two'), tooltip: 'Heading two' },
        { type: actionTypes.HEADING_THREE, active: Helpers.isBlockActive(editor, 'heading-three'), tooltip: 'Heading three' },
        { type: actionTypes.HEADING_FOUR, active: Helpers.isBlockActive(editor, 'heading-four'), tooltip: 'Heading four' },
        { type: actionTypes.HEADING_FIVE, active: Helpers.isBlockActive(editor, 'heading-five'), tooltip: 'Heading five' }
      ],
      onClick: handleToolbarButtonClick
    };

    return <ToolbarAnchor {...anchorProperties} />;

  }

  function renderMarkButtonsAnchor() {

    const anchorProperties = {
      editor,
      dynamicContentContainerRefrence,
      marks: [
        { type: actionTypes.BOLD, active: Helpers.isBoldMarkActive(editor), tooltip: 'Bold' },
        { type: actionTypes.ITALIC, active: Helpers.isItalicMarkActive(editor), tooltip: 'Italic' },
        { type: actionTypes.UNDERLINE, active: Helpers.isUnderlineMarkActive(editor), tooltip: 'Underline' },
        { type: actionTypes.STRIKE_THROUGH, active: Helpers.isStrikeThroughMarkActive(editor), tooltip: 'Strike through' }
      ],
      onClick: handleToolbarButtonClick
    };

    return <ToolbarAnchor {...anchorProperties} />;

  }

  function renderNodeTypeButtonsAnchor() {

    const anchorProperties = {
      editor,
      dynamicContentContainerRefrence,
      group: 'list',
      marks: [
        { type: actionTypes.BULLET_LIST, active: Helpers.isBlockActive(editor, 'bulleted-list'), tooltip: 'Bullet list' },
        { type: actionTypes.NUMBER_LIST, active: Helpers.isBlockActive(editor, 'numbered-list'), tooltip: 'Number list' },
        { type: actionTypes.CHECK_LIST, active: Helpers.isBlockActive(editor, 'check-list-item'), tooltip: 'Checklist' }
      ],
      onClick: handleToolbarButtonClick
    };

    return <ToolbarAnchor {...anchorProperties} />;

  }

  function renderQuoteButton() {

    return <React.Fragment>

      {renderButton(actionTypes.QUOTE_TEXT, 'quote-text-toggle-icon', void 0, 'Quoted text')}

    </React.Fragment>;

  }

  function renderLinkButtons() {

    const buttonAttributes = {
      className: 'inline-toolbar-button link-toggle-button',
      onClick: handleToggleLinkButtonClick
    };
    const iconAttributes = {
      className: 'inline-toolbar-button-icon link-toggle-icon'
    };

    if (Helpers.isLinkActive(editor) === true) {
      buttonAttributes.className += ' mark-active';
    }

    return <React.Fragment>
      <x-tooltip text="Add hyperlink">
        <button {...buttonAttributes}>
          <ToolbarLinkIcon {...iconAttributes} />
        </button>
      </x-tooltip>
    </React.Fragment>;

  }

  function renderConnectedPageLinkButtonIcon() {

    if (rootState.connectedPageLinkButtonWorking) {
      return <ht-spinner size="tiny"/>;
    }
    const iconAttributes = {
      className: 'inline-toolbar-button-icon link-toggle-icon'
    };

    return <ToolbarLinkIcon {...iconAttributes} />;

  }

  function renderConnectedPageLinkButton() {

    const buttonAttributes = {
      className: 'inline-toolbar-button link-toggle-button',
      onClick() {
        handleToggleConnectedPageLinkButtonClick();
      },
      disabled: rootState.connectedPageLinkButtonWorking
    };

    if (Helpers.dataCollection.isConnected(editor) === true) {
      buttonAttributes.className += ' mark-active';
    }

    return <React.Fragment>

      <button {...buttonAttributes}>
        {renderConnectedPageLinkButtonIcon()}
      </button>

    </React.Fragment>;

  }

  function renderColorButtons() {

    const textColorControlAttributes = {
      className: 'inline-toolbar-button color-button',
      onClick() {
        toggleTextColorOptionsDisplay();
      }
    };
    const backgroundColorControlAttributes = {
      className: 'inline-toolbar-button color-button',
      onClick() {
        toggleBackgroundColorOptionsDisplay();
      }
    };

    if (Helpers.textAttributeExists(editor, 'textColor') === true) {
      textColorControlAttributes.className += ' mark-active';
    }

    if (Helpers.textAttributeExists(editor, 'backgroundColor') === true) {
      backgroundColorControlAttributes.className += ' mark-active';
    }

    return <React.Fragment>
      <x-tooltip text="Text color">
        <button {...textColorControlAttributes}>
          <ToolbarTextColorIcon className="font-color-toggle-icon" />
        </button>
      </x-tooltip>
      <x-tooltip text="Text background color">
        <button {...backgroundColorControlAttributes}>
          <ToolbarBackgroundColorIcon className="background-color-toggle-icon" />
        </button>
      </x-tooltip>

    </React.Fragment>;

  }

  function renderColorButtonsSection() {

    return <div className='toolbar-section color-buttons-section'>

      {renderColorButtons()}

    </div>;

  }

  function tryRenderFontWeightSelector() {

    if (rootState.displayFontWeightSelector === false) {
      return;
    }

    const attribute = 'fontWeight';
    let fontWeight = Helpers.getTextAttribute(editor, attribute);

    const properties = {
      fontWeight,
      onChange(fontWeight) {
        applyTextAttributeWithSelection(attribute, fontWeight.value);
        hideFontWeightSelector();
      }
    };

    return <FontWeightSelector {...properties} />;

  }

  function tryRenderFontSizeSelector() {

    if (rootState.displayFontSizeSelector === false) {
      return;
    }
    const filterOptions = {
      at: editor.selection,
      match: (node) => SlateElement.isElement(node),
      mode: 'lowest'
    };
    const [nodeEntry] = Editor.nodes(editor, filterOptions);

    const attribute = 'fontSize';
    let fontSize = Helpers.getTextAttribute(editor, attribute) || Helpers.text.determineFontSize(nodeEntry[0]);

    const properties = {
      type: 'range',
      size: fontSize,
      min: 8,
      max: 100,
      step: .5,
      onChange(size) {
        applyTextAttributeWithSelection(attribute, size);
      }
    };

    return <FontSizeInput {...properties} />;

  }

  function renderFontFamilyButton() {

    const attribute = 'fontFamily';
    let fontFamily = Helpers.getTextAttribute(editor, attribute);

    const properties = {
      fontFamily,
      displaySelector: rootState.displayFontFamilySelector,
      onToggle: toggleFontFamilySelector,
      onChange(value) {
        applyTextAttributeWithSelection(attribute, value);
      },
      onClose: hideFontFamilySelector
    };

    return <FontFamilyButton {...properties} />;

  }

  function renderFontSizeButton() {

    const fontSizeButtonAttributes = {
      className: 'inline-toolbar-button font-size-button',
      onClick() {
        toggleFontSizeSelector();
      }
    };

    return <div className="font-size-button-section">

      <x-tooltip text="Font size">
        <button {...fontSizeButtonAttributes}>
          <FontSizeIcon />
        </button>
      </x-tooltip>
      {tryRenderFontSizeSelector()}

    </div>;

  }

  function renderFontWeightButton() {

    const fontWeightButtonAttributes = {
      className: 'inline-toolbar-button font-weight-button',
      onClick() {
        toggleFontWeightSelector();
      }
    };

    return <div className="font-weight-button-section">

      <x-tooltip text="Font weight">
        <button {...fontWeightButtonAttributes}>
          <FontWeightIcon />
        </button>
      </x-tooltip>
      {tryRenderFontWeightSelector()}

    </div>;

  }

  function tryRenderLineHeightSelector() {

    if (rootState.displayLineHeightSelector === false) {
      return;
    }

    const attribute = 'lineHeight';
    let lineHeight = Helpers.getTextAttribute(editor, attribute);

    const properties = {
      size: lineHeight || 22.5,
      min: 8,
      max: 100,
      step: .5,
      onChange(size) {
        applyTextAttributeWithSelection(attribute, size);
      }
    };

    return <FontSizeInput {...properties} />;

  }

  function renderLineHeightButton() {

    const lineHeightButtonAttributes = {
      className: 'inline-toolbar-button line-height-button',
      onClick() {
        toggleLineHeightSelector();
      }
    };

    return <div className="line-height-button-section">

      <x-tooltip text="Line height">
        <button {...lineHeightButtonAttributes}>
          <LineHeightIcon />
        </button>
      </x-tooltip>
      {tryRenderLineHeightSelector()}

    </div>;

  }

  function renderFontButtons() {

    return <React.Fragment>
      {renderFontFamilyButton()}
      {renderFontSizeButton()}
      {renderFontWeightButton()}
      {renderLineHeightButton()}
    </React.Fragment>;

  }

  function renderFontButtonsSection() {

    return <div className="toolbar-section">
      {renderFontButtons()}
    </div>;

  }

  function renderUnformatButton() {

    const textColorControlAttributes = {
      className: 'inline-toolbar-button unformat-button',
      onClick() {
        handleUnformatControlClick();
      }
    };

    return <div className='toolbar-section unformat-buttons-section'>
      <x-tooltip text="Remove formating">
        <button {...textColorControlAttributes}>
          <UnformatIcon className="unformat-toggle-icon" />
        </button>
      </x-tooltip>
    </div>;

  }

  function renderColorPicker(attribute) {

    let color = Helpers.getTextAttribute(editor, attribute);

    if (color === null) {
      color = '';
    }

    const colorPickerProperties = {
      color,
      onChange(color) {
        handleColorSelection(attribute, color);
      }
    };

    return <HelptapColorPicker {...colorPickerProperties} />;

  }

  function renderBackgroundColorOptions() {

    if (rootState.displayBackgroundColorOptions === false) {
      return;
    }

    const backgroundColorOptionsAttributes = {
      className: 'editor-toolbar-text-color-options',
      ref: toolbarBackgroundColorOptionsReference
    };

    return <div {...backgroundColorOptionsAttributes}>
      {renderColorPicker('backgroundColor')}
    </div>;

  }

  function renderTextColorOptions() {

    if (rootState.displayTextColorOptions === false) {
      return;
    }

    const textColorOptionsAttributes = {
      className: 'editor-toolbar-text-color-options',
      ref: toolbarTextColorOptionsReference
    };

    return <div {...textColorOptionsAttributes}>
      {renderColorPicker('textColor')}
    </div>;

  }

  function tryRenderHeadingButtonsSection() {

    if (isTextNode() === true) {
      return;
    }

    if (Helpers.canRenderHeadingButtonsSection(editor) === false) {
      return;
    }

    return <div className="toolbar-section">
      {renderHeadingButtonsAnchor()}
    </div>;

  }

  function tryRenderNodeTypeButtons() {

    if (Helpers.dataCollection.isDataCollectionDescendantSelected(editor) === true) {
      return;
    }

    if (Helpers.dataCollection.isDataCollectionRowPageNodeDescendant(editor) === true) {
      return;
    }

    if (Helpers.dataCollection.isDatabaseConnectedNodeSelected(editor) === true) {
      return;
    }

    if (isTextNode() === true) {
      return;
    }

    if (Helpers.canRenderNodeTypeButtons(editor) === false) {
      return false;
    }

    return <div className='toolbar-section'>
      {renderNodeTypeButtonsAnchor()}
    </div>;

  }

  function tryRenderQuoteButtonSection() {

    if (isTextNode() === true) {
      return;
    }

    if (Helpers.canRenderQuoteButtonSection(editor) === false) {
      return false;
    }

    return <div className='toolbar-section'>
      {renderQuoteButton()}
    </div>;

  }

  function tryRenderAlignmentButtonsSection() {

    if (Helpers.canRenderAlignmentButtonsSection(editor) === false) {
      return false;
    }

    const leftMarkActive = Helpers.isAlignmentMarkActive(editor, 'left');
    const centerMarkActive = Helpers.isAlignmentMarkActive(editor, 'center');
    const rightMarkActive = Helpers.isAlignmentMarkActive(editor, 'right');

    const anchorProperties = {
      editor,
      dynamicContentContainerRefrence,
      group: 'alignment',
      marks: [
        { type: actionTypes.ALIGN_LEFT, active: leftMarkActive, tooltip: 'Align left' },
        { type: actionTypes.ALIGN_CENTER, active: centerMarkActive, tooltip: 'Align center' },
        { type: actionTypes.ALIGN_RIGHT, active: rightMarkActive, tooltip: 'Align right' }
      ],
      onClick: handleToolbarButtonClick
    };

    return <ToolbarAnchor {...anchorProperties} />;

  }

  function tryRenderConnectedPageLinkButtons() {

    if (Helpers.dataCollection.isDataCollectionDescendantSelected(editor) === false) {
      return;
    }

    // if (Helpers.dataCollection.isDataCollectionRowPageNodeDescendant(editor) === false) {
    //   return;
    // }

    if (Helpers.dataCollection.isDatabaseConnectedNodeSelected(editor) === false) {
      return;
    }

    if (Helpers.canRenderLinkButtonsSection(editor) === false) {
      return false;
    }

    return <div className='toolbar-section'>
      {renderConnectedPageLinkButton()}
    </div>;

  }

  function tryRenderLinkButtons() {

    if (Helpers.dataCollection.isDataCollectionDescendantSelected(editor) === true) {
      return;
    }

    if (Helpers.dataCollection.isDataCollectionRowPageNodeDescendant(editor) === true) {
      return;
    }

    if (Helpers.dataCollection.isDatabaseConnectedNodeSelected(editor) === true) {
      return;
    }

    if (Helpers.canRenderLinkButtonsSection(editor) === false) {
      return false;
    }

    return <div className='toolbar-section'>
      {renderLinkButtons()}
    </div>;

  }

  function tryRenderAIButton() {

    if ((props.disabledElements || []).includes('ask-ai') === true) {
      return;
    }

    return <div className="toolbar-section">
      {renderAskAIButton()}
    </div>;

  }

  function renderToolbar() {

    if (rootState.currentNode === null) {
      return;
    }

    return <div className="toolbar-buttons-container editor-tool-container">

      {tryRenderHeadingButtonsSection()}

      <div className="toolbar-section">
        {renderMarkButtonsAnchor()}
      </div>

      {tryRenderNodeTypeButtons()}

      {tryRenderQuoteButtonSection()}

      {tryRenderAlignmentButtonsSection()}

      {tryRenderConnectedPageLinkButtons()}

      {tryRenderLinkButtons()}

      {renderColorButtonsSection()}

      {renderFontButtonsSection()}

      {renderUnformatButton()}

    </div>;

  }

  function tryRenderColorTools() {

    if (rootState.displayColorButtons === false) {
      return;
    }

    const toolGroupProperties = {
      anchor: colorButtonsAchorReference,
      toolbarDisplayedSide: rootState.toolbarDisplayedSide,
      lastScrolledAt: rootState.lastScrolledAt
    };

    return <ToolGroup {...toolGroupProperties}>
      {renderColorButtons()}
    </ToolGroup>;

  }

  function tryRenderFontTools() {

    if (rootState.displayFontButtons === false) {
      return;
    }

    const toolGroupProperties = {
      anchor: fontButtonsAchorReference,
      toolbarDisplayedSide: rootState.toolbarDisplayedSide,
      lastScrolledAt: rootState.lastScrolledAt
    };

    return <ToolGroup {...toolGroupProperties}>
      {renderFontButtons()}
    </ToolGroup>;

  }

  function renderDynamicContent() {

    const properties = {
      className: 'editor-toolbar-dynamic-content-container',
      ref: dynamicContentContainerRefrence
    };

    return <div {...properties}>

      {renderTextColorOptions()}

      {renderBackgroundColorOptions()}

      {tryRenderColorTools()}

      {tryRenderFontTools()}

    </div>;

  }

  function renderAIInputRow() {

    if (props.aiInstructionEnabled !== true) {
      return;
    }

    let className = 'editor-inline-toolbar-ai-row';

    if (aiPanelVisibleReference.current === false) {
      className += ' hidden';
    }

    const containerAttributes = {
      className: className,
      ref: handleAIInputContainerReference
    };

    return <div {...containerAttributes}></div>;

  }

  function isInteractiveToolControl(target) {

    if (target === null || typeof target.closest !== 'function') {
      return false;
    }

    return target.closest('input, textarea, select') !== null;

  }

  function isInteractionEngaged() {

    return aiInputEngagedReference.current === true || toolInputEngagedReference.current === true;

  }

  function engageToolInput() {

    toolInputEngagedReference.current = true;

    if (editor.selection !== null) {
      savedToolSelectionReference.current = editor.selection;
    }

  }

  function disengageToolInput() {

    toolInputEngagedReference.current = false;
    savedToolSelectionReference.current = null;

  }

  function applyTextAttributeWithSelection(attribute, value) {

    const savedSelection = savedToolSelectionReference.current;

    if (savedSelection !== null) {
      if (editor.selection === null || Range.equals(editor.selection, savedSelection) === false) {
        Transforms.select(editor, savedSelection);
      }
    }

    Helpers.setTextAttribute(editor, attribute, value);

  }

  function handleToolbarMouseDown(event) {

    const aiInputComponent = aiInputComponentReference.current;

    if (aiInputComponent !== null && aiInputComponent.containsTarget(event.target) === true) {
      return;
    }

    if (isInteractiveToolControl(event.target) === true) {
      engageToolInput();
      return;
    }

    event.preventDefault();

  }

  function renderMain() {

    if (nodeMenuState.isOpen === true) {
      return;
    }

    if (editor.selection === null && isInteractionEngaged() === false) {
      return;
    }

    if (editor.selection !== null && Helpers.isCollapsed(editor) === true && isInteractionEngaged() === false) {
      return;
    }

    const toolBarMainAttributes = {
      className: 'editor-inline-toolbar-main',
      ref: toolbarReference,
      onMouseDown: handleToolbarMouseDown,
      style: {
        zoom: props.zoom
      }
    };

    return <div {...toolBarMainAttributes}>

      {renderAIInputRow()}

      {renderToolbar()}

      {renderDynamicContent()}

    </div>;

  }

  return <Portal>
    {renderMain()}
  </Portal>;

}

export default Toolbar;